import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { StudyItem, Top3HookState, Top3Payload } from '@/types/top3';

/** Coerce any reasonable shape into Top3Payload */
function coerceTop3(obj: any): Top3Payload | null {
  if (!obj) return null;

  // Handle direct array case (n8n writes raw array to top3_studies)
  if (Array.isArray(obj)) {
    return { 
      generated_at: new Date().toISOString(), 
      source: 'n8n', 
      items: obj 
    };
  }

  if (typeof obj !== 'object') return null;

  // Accept a few common object shapes that n8n might send
  const items =
    Array.isArray(obj.items) ? obj.items
    : Array.isArray(obj.studies) ? obj.studies
    : Array.isArray(obj.top3_studies) ? obj.top3_studies
    : Array.isArray(obj?.data?.items) ? obj.data.items
    : Array.isArray(obj?.data?.studies) ? obj.data.studies
    : Array.isArray(obj?.data?.top3_studies) ? obj.data.top3_studies
    : Array.isArray(obj?.result?.items) ? obj.result.items
    : Array.isArray(obj?.response?.items) ? obj.response.items
    : null;

  if (!items) {
    return null;
  }

  const generated_at =
    typeof obj.generated_at === 'string' && obj.generated_at
      ? obj.generated_at
      : typeof obj.timestamp === 'string' && obj.timestamp
      ? obj.timestamp
      : null;

  const source =
    typeof obj.source === 'string' && obj.source ? obj.source : 'n8n';

  return { generated_at: generated_at ?? new Date().toISOString(), source, items };
}

/** Accepts JSONB/object or stringified JSON, with items OR studies */
function tryParseTop3(input: unknown): Top3Payload | null {
  if (!input) return null;

  if (typeof input === 'object' && input !== null) {
    return coerceTop3(input as any);
  }

  if (typeof input === 'string') {
    const raw = input.trim();

    // direct JSON
    if (raw.startsWith('{') || raw.startsWith('[')) {
      try {
        const parsed = JSON.parse(raw);
        return coerceTop3(parsed);
      } catch (e) {
        // fall through
      }
    }

    // embedded JSON block (for cases where n8n might wrap JSON in text)
    const start = raw.indexOf('{');
    const end = raw.lastIndexOf('}');
    if (start !== -1 && end > start) {
      try {
        const parsed = JSON.parse(raw.slice(start, end + 1));
        return coerceTop3(parsed);
      } catch (e) {
        // ignore parsing errors
      }
    }
  }

  return null;
}

export function useTop3Studies(userId: string | null): Top3HookState {
  const [items, setItems] = useState<StudyItem[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!userId);
  const [error, setError] = useState<string | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);
    try {
      // Include BOTH timestamp variants just in case your n8n writes one or the other
      const { data, error } = await supabase
        .from('profiles')
        .select('top3_studies, top3_studies_updated_at, top3_updated_at')
        .eq('user_id', userId)
        .single();

      if (error) {
        setError(error.message || 'Kon top-3 niet laden');
        setIsLoading(false);
        return;
      }

      const payload = tryParseTop3(data?.top3_studies);
      
      if (payload && Array.isArray(payload.items)) {
        setItems(payload.items.slice(0, 3));
        setGeneratedAt(
          payload.generated_at ||
          (data as any)?.top3_studies_updated_at ||
          (data as any)?.top3_updated_at ||
          null
        );
      } else {
        setItems([]);
        setGeneratedAt(
          (data as any)?.top3_studies_updated_at ||
          (data as any)?.top3_updated_at ||
          null
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Onbekende fout bij laden');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Initial load
  useEffect(() => {
    if (userId) fetchOnce();
  }, [userId, fetchOnce]);

  // Realtime updates for this user's profile row
  useEffect(() => {
    if (!userId) return;

    // Cleanup previous channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(`top3-studies-updates:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${userId}`,
        },
        async () => {
          await fetchOnce();
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [userId, fetchOnce]);

  return {
    items,
    generatedAt,
    isLoading,
    hasData: items.length > 0,
    error,
    refetch: fetchOnce,
  };
}
